import React, { useEffect, useState } from "react";
import BreadCrumbs from "../../components/common/BreadCrumbs";
import Loader from "../../components/loader/Loading";
import { getReq } from "../../apis/api";
import { CSVLink } from "react-csv";
import PriceListTable from "../../components/DataTable/PriceListTable";
import { useSnackbar } from 'notistack';
import { money, date, month } from "../../utils/format";
import { FilterBar, DateField, BarEnd } from "../../components/common/FilterControls";
import ProfitLossStatement, { INCOME_LINES, EXPENSE_LINES, GST_LINES, PROFIT_LABEL } from "../../components/common/ProfitLossStatement";

// SGEW 18 Oct (owner decision, final: "Without GST, purchases basis"): Profit & Loss for the chosen dates - every figure
// from the server (GET profitLoss, App\Libraries\ProfitLoss), the statement on top and, under Income / Expense, the
// documents behind each line. Export: one CSV of the statement and every document.

const pad = (n) => String(n).padStart(2, "0");
const localDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// the fields each table's search box looks in (the rows' own fields)
const PARTY_FILTERS = [
  { name: "Invoice", selector: "invoice_number" },
  { name: "Party", selector: "party_name" },
];
const SALE_FILTERS = [
  { name: "Order", selector: "order_id" },
  { name: "Invoice", selector: "invoice_number" },
  { name: "Party", selector: "party_name" },
];
const PURCHASE_FILTERS = [
  { name: "Purchase Order", selector: "purchase_order_number" },
  { name: "Invoice", selector: "invoice_number" },
  { name: "Party", selector: "party_name" },
];
const SALARY_FILTERS = [
  { name: "Name", selector: "employee_name" },
  { name: "Month", selector: (row) => `${row.month || ""} ${month(row.month)}` }, // as stored and as shown (Feb 2026)
];
const RETURN_FILTERS = [
  { name: "Credit Note", selector: "credit_note_no" },
  { name: "Party", selector: "party_name" },
  { name: "Order", selector: "order_id" },
];
const SERVICE_FILTERS = [
  { name: "Service", selector: "service_invoice_number" },
  { name: "Party", selector: "party_name" },
];
const SCRAP_FILTERS = [{ name: "Date", selector: (row) => date(row.date) }];
const INCOME_FILTERS = [
  { name: "Type", selector: "income_type" },
  { name: "Party", selector: "party_name" },
];
const VENDOR_FILTERS = [
  { name: "Payment", selector: "vendor_id" },
  { name: "Name", selector: "party_name" },
];

const EMPTY = { sales: [], returns: [], services: [], scrap: [], income: [], purchases: [], carriage: [], logistics: [], salaries: [], vendor: [] };

const num = (v) => Number(v) || 0;
const rupees = (col) => ({ selector: (row) => num(row[col]), format: (row) => money(row[col]), sortable: true, width: "130px" });
const index = { name: "#", selector: (_, i) => i + 1, sortable: false, width: "64px" };

const ProfitLossReport = () => {
  const today = new Date();
  const [isLoading, setIsLoading] = useState(false);
  const [tab, setTab] = useState("Income");
  const [startDate, setStartDate] = useState(localDay(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [endDate, setEndDate] = useState(localDay(today));
  const [summary, setSummary] = useState(null);
  const [period, setPeriod] = useState(null); // the dates of the figures on screen
  const [rows, setRows] = useState(EMPTY);
  const { enqueueSnackbar } = useSnackbar();

  const fetchProfitLossReport = async () => {
    if (!startDate || !endDate) {
      enqueueSnackbar("Please select both start and end dates", { variant: "warning" });
      return;
    }
    if (startDate > endDate) {
      enqueueSnackbar("The start date must not be after the end date.", { variant: "warning" });
      return;
    }
    try {
      setIsLoading(true);
      const payload = await getReq(`/profitLoss?start_date=${startDate}&end_date=${endDate}`);
      setSummary(payload?.summary || null);
      setPeriod(payload?.summary ? { from: payload.start_date || startDate, to: payload.end_date || endDate } : null);
      setRows({
        sales: payload?.sales_data || [],
        returns: payload?.returns_data || [],
        services: payload?.services_data || [],
        scrap: payload?.scrap_data || [],
        income: payload?.income_data || [],
        purchases: payload?.purchase_data || [],
        carriage: payload?.carriage_data || [],
        logistics: payload?.logistics_data || [],
        salaries: payload?.salary_data || [],
        vendor: payload?.vendor_data || [],
      });
    } catch (error) {
      console.error("Error", error);
      // RPT-13b: a failed request must not leave a clean-looking zero (or the previous) report on screen
      setSummary(null);
      setPeriod(null);
      setRows(EMPTY);
      enqueueSnackbar(error?.response?.data?.message || "Error in fetching Profit & Loss report", { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProfitLossReport(); // this month so far, on opening
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Table columns (FIX_RULES 10.3: selector stays raw for sorting, format is the one display format)
  const party = [
    { name: "Party Name", selector: (row) => row.party_name || "N/A", sortable: true, wrap: true },
    { name: "GSTIN", selector: (row) => row.gstin_number || "N/A", sortable: true, wrap: true },
  ];
  const salesColumns = [
    index,
    { name: "Order", selector: (row) => row.order_id || "N/A", sortable: true },
    { name: "Invoice", selector: (row) => row.invoice_number || "N/A", sortable: true },
    ...party,
    { name: "Order Date", selector: (row) => row.order_date || "", format: (row) => date(row.order_date), sortable: true },
    { name: "Without GST", ...rupees("amount_without_gst") },
    { name: "Shipping", ...rupees("shipping_amount") },
    { name: "GST", ...rupees("gst_amount") },
    { name: "With GST", ...rupees("total_amount") },
  ];
  const returnsColumns = [
    index,
    { name: "Credit Note", selector: (row) => row.credit_note_no || "N/A", sortable: true },
    { name: "Order", selector: (row) => row.order_id || "N/A", sortable: true },
    { name: "Party Name", selector: (row) => row.party_name || "N/A", sortable: true, wrap: true },
    { name: "Date", selector: (row) => row.date || "", format: (row) => date(row.date), sortable: true },
    { name: "Without GST", ...rupees("taxable_amount") },
    { name: "GST", ...rupees("gst_amount") },
    { name: "With GST", ...rupees("total_amount") },
  ];
  const servicesColumns = [
    index,
    { name: "Service", selector: (row) => row.service_invoice_number || "N/A", sortable: true },
    { name: "Party Name", selector: (row) => row.party_name || "N/A", sortable: true, wrap: true },
    { name: "Date", selector: (row) => row.date || "", format: (row) => date(row.date), sortable: true },
    { name: "Status", selector: (row) => (row.warranty ? `${row.status} - warranty, not charged` : row.status), sortable: true, wrap: true },
    { name: "Without GST", ...rupees("amount_without_gst") },
    { name: "GST", ...rupees("gst_amount") },
    { name: "Charged, with GST", ...rupees("total_amount") },
  ];
  const scrapColumns = [
    index,
    { name: "Sale", selector: (row) => `Scrap sale #${row.id}`, sortable: true },
    { name: "Date", selector: (row) => row.date || "", format: (row) => date(row.date), sortable: true },
    { name: "Weight (kg)", selector: (row) => num(row.weight), sortable: true },
    { name: "Amount", ...rupees("amount") },
  ];
  const incomeColumns = [
    index,
    { name: "Type", selector: (row) => row.income_type || "N/A", sortable: true, wrap: true },
    { name: "Description", selector: (row) => row.description || "-", sortable: true, wrap: true },
    { name: "Party Name", selector: (row) => row.party_name || "N/A", sortable: true, wrap: true },
    { name: "Date", selector: (row) => row.date || "", format: (row) => date(row.date), sortable: true },
    { name: "Amount", ...rupees("amount") },
  ];
  const purchaseColumns = [
    index,
    { name: "Purchase Order", selector: (row) => row.purchase_order_number || "N/A", sortable: true },
    { name: "Invoice", selector: (row) => row.invoice_number || "N/A", sortable: true },
    ...party,
    // SGEW 18 Oct (owner: "prefer the supplier invoice date"): a purchase counts on its supplier invoice's date
    { name: "Invoice Date", selector: (row) => row.invoice_date || "", format: (row) => date(row.invoice_date), sortable: true },
    { name: "Without GST", ...rupees("total_invoice_amount") },
    // the order's GST % of every line - "12% / 18%" when they differ
    { name: "GST %", selector: (row) => row.cgstSgst || "-", sortable: true },
    { name: "GST", ...rupees("gst_amount") },
    { name: "Bill, with GST", ...rupees("grandTotal") },
  ];
  const chargeColumns = (col) => [
    index,
    { name: "Purchase Order", selector: (row) => row.purchase_order_number || "N/A", sortable: true },
    { name: "Invoice", selector: (row) => row.invoice_number || "N/A", sortable: true },
    ...party,
    { name: "Invoice Date", selector: (row) => row.invoice_date || "", format: (row) => date(row.invoice_date), sortable: true },
    { name: "Amount", ...rupees(col) },
  ];
  // NEW-51: the approved salaries whose month ends in the range (Approvals > Salary)
  const salaryColumns = [
    index,
    { name: "Name", selector: (row) => row.employee_name || "N/A", sortable: true },
    { name: "Month", selector: (row) => row.month || "N/A", format: (row) => month(row.month), sortable: true },
    { name: "Paid Days", selector: (row) => Math.max(0, 30 - Number(row.unpaid_days || 0)), sortable: true },
    { name: "Basic Pay", ...rupees("basic_pay") },
    { name: "Overtime Pay", ...rupees("overtime_pay") },
    { name: "Amount", ...rupees("total_pay") },
  ];
  const vendorColumns = [
    index,
    { name: "Payment", selector: (row) => row.vendor_id || "N/A", sortable: true },
    { name: "Name", selector: (row) => row.party_name || "N/A", sortable: true, wrap: true },
    { name: "Booked", selector: (row) => row.date || "", format: (row) => date(row.date), sortable: true },
    { name: "Work From", selector: (row) => row.date_from || "", format: (row) => date(row.date_from), sortable: true },
    { name: "Work To", selector: (row) => row.date_to || "", format: (row) => date(row.date_to), sortable: true },
    { name: "Amount", ...rupees("amount") },
  ];

  const s = summary || {};
  const label = (lines, key) => lines.find((l) => l.key === key).label;
  const sections = {
    Income: [
      { title: label(INCOME_LINES, "sales"), total: s.sales, columns: salesColumns, data: rows.sales, filters: SALE_FILTERS },
      { title: label(INCOME_LINES, "returns"), total: s.returns, columns: returnsColumns, data: rows.returns, filters: RETURN_FILTERS },
      { title: label(INCOME_LINES, "services"), total: s.services, columns: servicesColumns, data: rows.services, filters: SERVICE_FILTERS },
      { title: label(INCOME_LINES, "scrap_sales"), total: s.scrap_sales, columns: scrapColumns, data: rows.scrap, filters: SCRAP_FILTERS },
      { title: label(INCOME_LINES, "other_income"), total: s.other_income, columns: incomeColumns, data: rows.income, filters: INCOME_FILTERS },
    ],
    Expense: [
      { title: label(EXPENSE_LINES, "purchases"), total: s.purchases, columns: purchaseColumns, data: rows.purchases, filters: PURCHASE_FILTERS },
      { title: label(EXPENSE_LINES, "carriage"), total: s.carriage, columns: chargeColumns("carriage_amount"), data: rows.carriage, filters: PARTY_FILTERS },
      { title: label(EXPENSE_LINES, "logistics"), total: s.logistics, columns: chargeColumns("logistics_amount"), data: rows.logistics, filters: PARTY_FILTERS },
      { title: label(EXPENSE_LINES, "salaries"), total: s.salaries, columns: salaryColumns, data: rows.salaries, filters: SALARY_FILTERS },
      { title: label(EXPENSE_LINES, "vendor_payments"), total: s.vendor_payments, columns: vendorColumns, data: rows.vendor, filters: VENDOR_FILTERS },
    ],
  };

  // CSV: the statement, then every document behind it - one row shape throughout
  const csvHeaders = [
    { label: "Section", key: "section" },
    { label: "Line / Document", key: "document" },
    { label: "Date", key: "date" },
    { label: "Party", key: "party" },
    { label: "GSTIN", key: "gstin" },
    { label: "Without GST", key: "without_gst" },
    { label: "GST", key: "gst" },
    { label: "With GST", key: "with_gst" },
    { label: "Note", key: "note" },
  ];
  const csvRow = (section, document, values = {}) => ({
    section, document, date: "", party: "", gstin: "", without_gst: "", gst: "", with_gst: "", note: "", ...values,
  });
  const lineRows = (section, lines, column) =>
    lines.map((l) => csvRow(section, l.label, { [column]: num(s[l.key]) * (l.minus ? -1 : 1) }));
  const csvData = !summary ? [] : [
    csvRow("Period", `${date(period?.from)} to ${date(period?.to)}`),
    ...lineRows("Income", INCOME_LINES, "without_gst"),
    ...lineRows("Expense", EXPENSE_LINES, "without_gst"),
    csvRow("Result", PROFIT_LABEL, { without_gst: num(s.profit_loss), note: num(s.profit_loss) >= 0 ? "Profit" : "Loss" }),
    ...lineRows("GST (not in profit)", GST_LINES, "gst"),
    ...rows.sales.map((r) => csvRow("Sales", [r.order_id, r.invoice_number].filter(Boolean).join(" / "), {
      date: r.order_date, party: r.party_name || "", gstin: r.gstin_number || "", without_gst: num(r.amount_without_gst),
      gst: num(r.gst_amount), with_gst: num(r.total_amount), note: num(r.shipping_amount) ? `shipping ${num(r.shipping_amount)} included` : "",
    })),
    ...rows.returns.map((r) => csvRow("Returns (credit notes)", r.credit_note_no || "", {
      date: r.date, party: r.party_name || "", gstin: r.gstin_number || "", without_gst: -num(r.taxable_amount),
      gst: -num(r.gst_amount), with_gst: -num(r.total_amount), note: r.order_id ? `order ${r.order_id}` : "",
    })),
    ...rows.services.map((r) => csvRow("Services", r.service_invoice_number || "", {
      date: r.date, party: r.party_name || "", gstin: r.gstin_number || "", without_gst: num(r.amount_without_gst),
      gst: num(r.gst_amount), with_gst: num(r.total_amount), note: r.warranty ? `${r.status}, warranty - not charged` : r.status,
    })),
    ...rows.scrap.map((r) => csvRow("Scrap sales", `Scrap sale #${r.id}`, {
      date: r.date, without_gst: num(r.amount), with_gst: num(r.amount), note: `${num(r.weight)} kg`,
    })),
    ...rows.income.map((r) => csvRow("Other income", r.income_type || "", {
      date: r.date, party: r.party_name || "", without_gst: num(r.amount), with_gst: num(r.amount), note: r.description || "",
    })),
    ...rows.purchases.map((r) => csvRow("Purchases received", [r.purchase_order_number, r.invoice_number].filter(Boolean).join(" / "), {
      date: r.invoice_date || r.order_date, party: r.party_name || "", gstin: r.gstin_number || "", without_gst: -num(r.total_invoice_amount),
      gst: -num(r.gst_amount), with_gst: -num(r.grandTotal), note: r.cgstSgst ? `GST ${r.cgstSgst}` : "",
    })),
    ...rows.carriage.map((r) => csvRow("Carriage", [r.purchase_order_number, r.invoice_number].filter(Boolean).join(" / "), {
      date: r.invoice_date || r.order_date, party: r.party_name || "", gstin: r.gstin_number || "", without_gst: -num(r.carriage_amount), with_gst: -num(r.carriage_amount),
    })),
    ...rows.logistics.map((r) => csvRow("Logistics", [r.purchase_order_number, r.invoice_number].filter(Boolean).join(" / "), {
      date: r.invoice_date || r.order_date, party: r.party_name || "", gstin: r.gstin_number || "", without_gst: -num(r.logistics_amount), with_gst: -num(r.logistics_amount),
    })),
    ...rows.salaries.map((r) => csvRow("Salaries", `Salary ${month(r.month)}`, {
      party: r.employee_name || "", without_gst: -num(r.total_pay), with_gst: -num(r.total_pay),
      note: `basic ${num(r.basic_pay)} + overtime ${num(r.overtime_pay)}`,
    })),
    ...rows.vendor.map((r) => csvRow("Vendor / job work", r.vendor_id || "", {
      date: r.date, party: r.party_name || "", without_gst: -num(r.amount), with_gst: -num(r.amount),
      note: `work ${date(r.date_from)} to ${date(r.date_to)}`,
    })),
  ];

  return (
    <div className="w-full h-full">
      <div className="flex justify-between items-center bg-white rounded-[0.42vw] w-full py-3 md:py-[0.5vw] my-[2vw] md:my-[1vw] px-[4vw] md:px-[2vw] ">
        <BreadCrumbs title={"Home / Dashboard / Reporting / Profit & Loss"} />
      </div>

      {/* SGEW 17 Oct: the report's dates in one labelled row (FilterControls), Fetch / Export at its end */}
      <FilterBar className="bg-white rounded-[0.42vw] w-full px-3 py-3 md:pl-6 md:pr-4 md:py-[0.8vw] mb-3 md:mb-[1vw] font-poppins">
        <DateField label="Start date" value={startDate} max={endDate} onChange={setStartDate} />
        <DateField label="End date" value={endDate} min={startDate} onChange={setEndDate} />
        <BarEnd>
          <button
            type="button"
            onClick={fetchProfitLossReport}
            className="sgew-btn sgew-btn-success"
          >
            Fetch
          </button>
          <CSVLink
            data={csvData}
            headers={csvHeaders}
            filename={`profit_loss_${period?.from || startDate}_${period?.to || endDate}.csv`}
            className="sgew-btn sgew-btn-primary"
          >
            Export
          </CSVLink>
        </BarEnd>
      </FilterBar>

      <main className="px-[4vw] md:px-0">
        {isLoading ? (
          <Loader />
        ) : (
          <>
            {summary ? (
              <>
                <p className="text-sm text-gray-600 font-poppins mb-2 px-1">
                  {date(period?.from)} to {date(period?.to)}
                </p>
                <ProfitLossStatement summary={summary} />
              </>
            ) : (
              <p className="text-gray-500 text-center font-poppins py-6">Choose the dates and press Fetch.</p>
            )}

            {/* the documents behind each line */}
            <div className="flex flex-wrap gap-2 mt-6 mb-3 md:mb-[1vw]">
              {Object.keys(sections).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  className={`sgew-tab ${tab === key ? "is-active" : ""}`}
                >
                  {key}
                </button>
              ))}
            </div>
            {sections[tab].map((sec) => (
              <div key={sec.title} className="mt-6">
                <h3 className="text-base md:text-lg font-semibold mb-2 font-poppins flex flex-wrap justify-between gap-2">
                  <span>{sec.title}</span>
                  <span className="tabular-nums">{summary ? money(sec.total) : "-"}</span>
                </h3>
                <PriceListTable columns={sec.columns} data={sec.data} filterColumns={sec.filters} />
              </div>
            ))}
          </>
        )}
      </main>
    </div>
  );
};

export default ProfitLossReport;
